/**
 * Calendar export — the zero-infrastructure fixture reminder. Generates an
 * RFC 5545 VEVENT and hands it to the browser as a download; works offline
 * and needs no notification permission or push pipeline.
 */
import type { Fixture } from './types';

function icsStamp(iso: string): string {
  // 2026-08-16T14:00:00Z → 20260816T140000Z
  return iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function escapeText(s: string): string {
  return s.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
}

export function fixtureToICS(fixture: Fixture, nowIso: string = new Date().toISOString()): string {
  const allDay = fixture.timeTbc && fixture.kickoffUtc === null && Boolean(fixture.kickoffDate);
  const start = allDay ? fixture.kickoffDate!.replace(/-/g, '') : icsStamp(fixture.kickoffUtc ?? '');
  // Football is two hours of calendar, give or take stoppage time.
  const end = allDay
    ? new Date(Date.parse(`${fixture.kickoffDate}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10).replace(/-/g, '')
    : icsStamp(new Date(Date.parse(fixture.kickoffUtc ?? '') + 2 * 3_600_000).toISOString());
  const homeAway = fixture.venue === 'H' ? 'vs' : fixture.venue === 'A' ? 'away at' : 'v';
  const summary = `Arsenal ${homeAway} ${fixture.opponent} (${fixture.comp})`;
  const location = fixture.ground ?? (fixture.venue === 'H' ? 'Emirates Stadium' : '');

  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//COYG//COYG//EN',
    'BEGIN:VEVENT',
    `UID:coyg-${fixture.id}@coyg.org`,
    `DTSTAMP:${icsStamp(nowIso)}`,
    `${allDay ? 'DTSTART;VALUE=DATE' : 'DTSTART'}:${start}`,
    `${allDay ? 'DTEND;VALUE=DATE' : 'DTEND'}:${end}`,
    `SUMMARY:${escapeText(summary)}`,
    location ? `LOCATION:${escapeText(location)}` : null,
    `DESCRIPTION:${escapeText(`Matchday. Open COYG: https://coyg.org/match/${encodeURIComponent(fixture.id)}`)}`,
    ...(allDay ? [] : ['BEGIN:VALARM', 'TRIGGER:-PT1H', 'ACTION:DISPLAY', `DESCRIPTION:${escapeText(`Kick-off in one hour — Arsenal ${homeAway} ${fixture.opponent}`)}`, 'END:VALARM']),
    'END:VEVENT',
    'END:VCALENDAR',
  ]
    .filter((line): line is string => line !== null)
    .join('\r\n');
}

export function downloadICS(fixture: Fixture): void {
  const blob = new Blob([fixtureToICS(fixture)], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `arsenal-${fixture.id}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/** Google Calendar's compact UTC stamp: 20260830T153000Z (no millis). */
function gcalStamp(iso: string): string {
  return iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

/**
 * A prefilled Google Calendar event URL — one tap opens the "new event" form
 * with the fixture's title, time, place, and a deep link back into COYG
 * already filled in, so the fan just hits Save. The primary "Add to calendar"
 * path on devices where Google Calendar is the calendar; the .ics download
 * stays as the Apple/Outlook fallback.
 */
export function googleCalendarUrl(fixture: Fixture): string {
  const allDay = fixture.timeTbc && fixture.kickoffUtc === null && Boolean(fixture.kickoffDate);
  const start = allDay ? fixture.kickoffDate!.replace(/-/g, '') : gcalStamp(fixture.kickoffUtc ?? '');
  const end = allDay
    ? new Date(Date.parse(`${fixture.kickoffDate}T00:00:00Z`) + 86_400_000).toISOString().slice(0, 10).replace(/-/g, '')
    : gcalStamp(new Date(Date.parse(fixture.kickoffUtc ?? '') + 2 * 3_600_000).toISOString());
  const homeAway = fixture.venue === 'H' ? 'vs' : fixture.venue === 'A' ? 'away at' : 'v';
  const text = `Arsenal ${homeAway} ${fixture.opponent}${fixture.comp ? ` (${fixture.comp})` : ''}`;
  const location = fixture.ground ?? (fixture.venue === 'H' ? 'Emirates Stadium' : '');
  const details = `Matchday. Open the COYG match edition: https://coyg.org/match/${encodeURIComponent(fixture.id)}`;
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text,
    dates: `${start}/${end}`,
    details,
  });
  if (location) params.set('location', location);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * The one "Add to calendar" entry point. Opens a prefilled Google Calendar
 * event in a new tab (one tap → ready to save). If the popup is blocked or
 * unavailable, falls back to the universal .ics download, which iOS opens
 * straight into Apple Calendar.
 */
export function addToCalendar(fixture: Fixture): void {
  try {
    const win = window.open(googleCalendarUrl(fixture), '_blank', 'noopener,noreferrer');
    if (win) return;
  } catch {
    /* popup blocked / sandboxed — fall through to the file */
  }
  downloadICS(fixture);
}
